import { Box, Button, Checkbox, Divider, Fade, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { useGlobals } from 'src/shared/contexts/appContext';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FundraiserTypeEnum } from 'src/modules/gift/models/fundraiserType.enum';
import { useWebFormArrays } from 'src/shared/hooks/useWebFormArrays';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { EmbededCodePreviewModal } from '../../../shared/components/organisms/embededCodePreviewModal';
import { FundraiserResponse } from '../models/fundraiserResponse';
import { FundraiserSettingsAutocomplete } from './fundraiserSettingsAutocomplete';

type FundraiserEmbedCodeProps = {
  fundraiserResponse: FundraiserResponse;
  embedType: string;
};

export const FundraiserSettingsEmbedCode = (props: FundraiserEmbedCodeProps) => {
  const { fundraiserResponse, embedType } = props;
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const { themeTemplateArray, langTemplateArray } = useWebFormArrays();

  const [displayCopyMessage, setDisplayCopyMessage] = useState<boolean>(false);
  const [template, setTemplate] = useState<SelectableItem>(themeTemplateArray[0]);
  const [language, setLanguage] = useState<SelectableItem>(langTemplateArray[0]);
  const [policyUrl, setPolicyUrl] = useState<string>(''); // För alla typer - Göra required?
  const [usePnr, setUsePnr] = useState<boolean>(false); // För typ 1 & 2
  const [usePostal, setUsePostal] = useState<boolean>(false); // För typ 1
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);

  ConsoleHelper.log('language', language);
  const getTemplate = () => {
    // Högtidsgåva har ingen inbäddnignskod än

    // För typ minnesgåva 1
    if (fundraiserResponse?.type === FundraiserTypeEnum.Minnesgåva && embedType === 'individual') {
      return `<!--- CODE TO EMBED --->
        <script src="https://forms.foreningssupport.se/js/embed/memorialgifts2.js"></script>
        <div id="fs-widget"></div>
        <script>
        GiftFormComponent({cId: "${currentUser?.custName}",
        pnr: ${usePnr}, postal: ${usePostal}, consent: true,
        policyUrl: '${policyUrl}',
        lang: '${language.value}',
        theme: '${template.value}', fId: "${fundraiserResponse?.fundraiser_id}", navigateScroll: function(){ var element = document.getElementById('fs-widget');element.scrollIntoView()}, resizeMe: function (size) {var element = document.getElementById('fs-widget');element.firstElementChild.setAttribute("style","height:" + size + "px");}}).render('#fs-widget');
        </script>
        <!-- END // CODE TO EMBED -->`;
    }
    // Ska bara vara för typ 2
    if (fundraiserResponse?.type === FundraiserTypeEnum.Gåva && embedType === 'individual') {
      return `<!--- CODE TO EMBED --->
        <script src="
        https://forms.foreningssupport.se/js/embed/gifts2.js"></script>
        <div id="fs-widget"></div>
        <script>
        GiftFormComponent({cId: "${currentUser?.custName}",
        pnr: ${usePnr}, postal: false, consent: true,
        policyUrl: '${policyUrl}',
        lang: '${language.value}',
        theme: '${template.value}', fId: "${fundraiserResponse?.fundraiser_id}", navigateScroll: function(){ var element = document.getElementById('fs-widget');element.scrollIntoView()}, resizeMe: function (size) {var element = document.getElementById('fs-widget');element.firstElementChild.setAttribute("style","height:" + size + "px");}}).render('#fs-widget');
        </script>
        <!--- END // CODE TO EMBED --->`;
    }
    // Om typ 2 ska nedan visas under den "vanliga" för typ 2
    if (fundraiserResponse?.type === FundraiserTypeEnum.Gåva && embedType === 'donor') {
      return `<!--- CODE TO EMBED --->
        <script src="
        https://forms.foreningssupport.se/js/embed/donor.js"></script>
        <div id="fs-donor-widget"></div>
        <script>
        DonorFormComponent({cId: "${currentUser?.custName}",
        policyUrl: '${policyUrl}',
        lang: '${language.value}',
        theme: '${template.value}', fId: "${fundraiserResponse?.fundraiser_id}", navigateScroll: function(){ var element = document.getElementById('fs-donor-widget');element.scrollIntoView()}, resizeMe: function (size) {var element = document.getElementById('fs-donor-widget');element.firstElementChild.setAttribute("style","height:" + size + "px");}}).render('#fs-donor-widget');
        </script>
        <!-- END // CODE TO EMBED -->`;
    }
    if (fundraiserResponse?.type === FundraiserTypeEnum.Högtidsgåva && embedType === 'individual') {
      return `<!--- CODE TO EMBED --->
        <script src="https://forms.foreningssupport.se/js/embed/celebrationgifts.js"></script>
        <div id="fs-widget"></div>
        <script>
        GiftFormComponent({cId: "${currentUser?.custName}",
        pnr: ${usePnr}, postal: ${usePostal}, consent: true,
        policyUrl: '${policyUrl}',
        lang: '${language.value}',
        theme: '${template.value}', fId: "${fundraiserResponse?.fundraiser_id}", navigateScroll: function(){ var element = document.getElementById('fs-widget');element.scrollIntoView()}, resizeMe: function (size) {var element = document.getElementById('fs-widget');element.firstElementChild.setAttribute("style","height:" + size + "px");}}).render('#fs-widget');
        </script>
        <!-- END // CODE TO EMBED -->`;
    }
    return '';
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
          {embedType === 'individual' ? t('fundraiser:embedHeaderIndividualGift') : t('fundraiser:embedHeaderDonorGift')}
        </Typography>
        <Divider />
      </Grid>
      <Grid item xs={12} md={4}>
        <Grid item xs={12} mb={2}>
          <FundraiserSettingsAutocomplete
            id='template-picker'
            label={t('fundraiser:selectTemplate')}
            options={themeTemplateArray}
            callback={(x) => setTemplate(x)}
          />
        </Grid>
        <Grid item xs={12} mb={2}>
          <FundraiserSettingsAutocomplete
            id='language-picker'
            label={t('fundraiser:selectLanguage')}
            options={langTemplateArray}
            callback={(x) => setLanguage(x)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('fundraiser:privacyPolicyUrl')}
            placeholder='https://'
            size='small'
            value={policyUrl}
            onChange={(e) => setPolicyUrl(e.target.value)}
          />
        </Grid>
        {embedType === 'individual' && (
          <Grid item xs={12} mt={1}>
            <FormControlLabel
              disableTypography
              control={<Checkbox checked={usePnr} onChange={(e) => setUsePnr(e.target.checked)} />}
              label={t('fundraiser:usePnr')}
            />
          </Grid>
        )}
        {(fundraiserResponse?.type === FundraiserTypeEnum.Minnesgåva || fundraiserResponse?.type === FundraiserTypeEnum.Högtidsgåva) && (
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={usePostal} onChange={(e) => setUsePostal(e.target.checked)} />}
              label={t('fundraiser:usePostal')}
              disableTypography
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} md={8}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            rows={10}
            multiline
            fullWidth
            label={t('fundraiser:embedCodeLabel')}
            value={getTemplate()}
            onClick={() => {
              navigator.clipboard.writeText(getTemplate());
              setDisplayCopyMessage(true);
              setTimeout(() => {
                setDisplayCopyMessage(false);
              }, 2000);
            }}
            InputProps={{ readOnly: true }}
            sx={{ cursor: 'pointer !important' }}
          />
          <Fade in={displayCopyMessage}>
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                borderRadius: 1,
                bottom: '2px',
                left: '2px',
                right: '2px',
                top: '2px',
                p: 2,
                backgroundColor: 'rgba(241,244,245,0.9)',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '3rem', mb: 2 }} />
              <Typography variant='h5'>{t('fundraiser:codeCopiedMessage')}</Typography>
            </Box>
          </Fade>
        </Box>
      </Grid>
      <Grid container item xs={12} flexDirection='row' justifyContent='flex-end' gap={2}>
        <Grid item xs={12} md='auto'>
          <Button
            variant='outlined'
            size='medium'
            fullWidth
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setShowPreviewModal(true)}
            disabled={!getTemplate()}
          >
            {t('fundraiser:preview')}
          </Button>
        </Grid>
        <Grid item xs={12} md='auto'>
          <Button
            variant='outlined'
            size='medium'
            fullWidth
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(getTemplate());
              setDisplayCopyMessage(true);
              setTimeout(() => {
                setDisplayCopyMessage(false);
              }, 2000);
            }}
          >
            {t('fundraiser:copyCode')}
          </Button>
        </Grid>
      </Grid>
      {showPreviewModal && (
        <EmbededCodePreviewModal modalOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} embeddedCode={getTemplate()} />
      )}
    </>
  );
};
